import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { getCategoriesList, updateCategory } from "../../../../../services/Admin/MenuApi";
import { resolveImageUrl } from "../../../../../services/indexMediaBaseUrl";
import CategoryDetailsDialog from "./CategoryDetailsDialog";
import AddCategoryDialog from "./AddCategoryDialog";
import EditeCategoryDialog from "./EditeCategoryDialog";
import { useAuth } from "../../../../../Context/AuthContext";

const isDeletedCategory = (category) => Number(category?.status) === 0;

const CategoryTapMain = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [deletingId, setDeletingId] = useState(null);
  const { hasPermission, userType, userRole } = useAuth();
  const canViewDeletedTab = userType === "admin" && userRole === "admin";

  const fetchCategories = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getCategoriesList();
      setCategories(res?.data || []);
    } catch (err) {
      setError(err?.message || "فشل تحميل التصنيفات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!canViewDeletedTab && activeTab === "deleted") {
      setActiveTab("active");
    }
  }, [activeTab, canViewDeletedTab]);

  const activeCategoriesCount = useMemo(
    () => categories.filter((category) => !isDeletedCategory(category)).length,
    [categories]
  );

  const deletedCategoriesCount = useMemo(
    () => categories.filter(isDeletedCategory).length,
    [categories]
  );

  const filteredCategories = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return categories.filter((category) => {
      const isDeleted = isDeletedCategory(category);
      if (activeTab === "active" && isDeleted) return false;
      if (activeTab === "deleted" && (!canViewDeletedTab || !isDeleted)) return false;

      if (!q) return true;

      const fields = [
        category?.name,
        category?.description,
        category?.name_en,
        category?.description_en,
      ];

      return fields.some((field) =>
        String(field || "").toLowerCase().includes(q)
      );
    });
  }, [categories, searchText, activeTab, canViewDeletedTab]);

  const handleOpenDetails = (categoryId) => {
    if (hasPermission("view")) {
      setSelectedId(categoryId);
      setDetailsOpen(true);
      return;
    }

    alert("ليس لديك الصلاحية لرؤية تفاصيل هذا التصنيف");
  };

  const handleSoftDeleteCategory = async (category) => {
    if (!category?.id || deletingId) return;

    const confirmed = window.confirm(`هل تريد حذف التصنيف "${category?.name || "-"}"؟`);
    if (!confirmed) return;

    setDeletingId(category.id);
    setError("");

    try {
      await updateCategory({
        ...category,
        id: category.id,
        name: category.name || "",
        name_en: category.name_en || "",
        description: category.description || "",
        description_en: category.description_en || "",
        image: category.image,
        status: 0,
      });

      setCategories((prev) =>
        prev.map((item) =>
          item.id === category.id ? { ...item, status: 0 } : item
        )
      );
      fetchCategories();
    } catch (err) {
      setError(err?.message || "فشل حذف التصنيف");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ color: "error.main", p: 2, mb: 2, bgcolor: "#fff5f5", borderRadius: 1 }}>
          {String(error)}
        </Box>
      )}

      {hasPermission("add") && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
            إضافة فئة
          </Button>
        </Box>
      )}

      <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="active" label={`التصنيفات (${activeCategoriesCount})`} />
          {canViewDeletedTab && (
            <Tab value="deleted" label={`المحذوفات (${deletedCategoriesCount})`} />
          )}
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="ابحث باسم الفئة أو الوصف"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {filteredCategories.length === 0 && (
        <Box
          sx={{
            p: 3,
            borderRadius: 1,
            backgroundColor: "grey.100",
            color: "text.secondary",
            mb: 2,
          }}
        >
          لا توجد نتائج مطابقة للبحث.
        </Box>
      )}

      <Grid container spacing={2}>
        {filteredCategories.map((category) => {
          const isDeleted = isDeletedCategory(category);
          const isDeleting = deletingId === category.id;

          return (
            <Grid item key={category.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  border: "1px solid",
                  borderColor: isDeleted ? "error.light" : "divider",
                  backgroundColor: isDeleted ? "#fff5f5" : "background.paper",
                  opacity: isDeleted ? 0.9 : 1,
                }}
              >
                <CardActionArea onClick={() => handleOpenDetails(category.id)}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={resolveImageUrl(category.image)}
                    alt={category.name}
                  />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {category.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={isDeleted ? "محذوف" : "متاح"}
                        color={isDeleted ? "error" : "success"}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {category.description}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 1 }}
                      color={isDeleted ? "error.main" : "success.main"}
                    >
                      {isDeleted ? "محذوف" : "متاح"}
                    </Typography>
                  </CardContent>
                </CardActionArea>

                <CardActions disableSpacing>
                  {hasPermission("edit") && (
                    <IconButton
                      aria-label="edit"
                      disabled={isDeleting}
                      onClick={() => {
                        setEditId(category.id);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  )}

                  {!isDeleted && (hasPermission("delete") || hasPermission("edit")) && (
                    <IconButton
                      aria-label="delete"
                      color="error"
                      disabled={isDeleting}
                      onClick={() => handleSoftDeleteCategory(category)}
                    >
                      {isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <CategoryDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        categoryId={selectedId}
      />
      <AddCategoryDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={() => {
          fetchCategories();
        }}
      />
      <EditeCategoryDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        categoryId={editId}
        onUpdated={() => {
          fetchCategories();
          setEditDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default CategoryTapMain;
